package ui

import enums.CommanderType
import io.kvision.core.*
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.utils.perc
import io.kvision.utils.px
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import structs.Commander
import structs.matchmaking.GameType

/**
 * Two-step wizard for picking a commander and starting a match.
 *
 * **Step 1 — "Select Commander":** The user searches and picks one of their
 * locally cached commanders. The list takes the full dialog height so the
 * user can read the commander card text (Empire, Type, Trait, description)
 * without fighting a tiny scrollbar. The **Next** button is disabled until
 * a commander is selected.
 *
 * **Step 2 — "Choose Game Settings":** The user picks the game type (Single
 * Player vs. Multiplayer) and, in Single Player mode, the AI opponent count.
 * In Multiplayer mode the AI opponent section is hidden because matchmaking
 * picks the player count. The **Back** button returns to Step 1, preserving
 * the selected commander. The **Play** button invokes [onConfirmSelection]
 * with the chosen commander, game type, and AI opponent count.
 *
 * Splitting into two steps (rather than cramming everything into one
 * 900px-tall dialog) is the fix for the "1, 2, 3, and 4 player options get
 * pushed off the bottom of the widget" bug introduced by the "Prep ams and
 * multiplayer" commit. With one decision per step, neither screen needs to
 * compete for vertical space — the commander list gets a real
 * [commander-selection-list] container with `min-height: 320px` instead of
 * being squeezed to a 140px strip.
 */
class CommanderSelectionDialog(
    commanders: List<Commander>,
    private val onConfirmSelection: ((Commander, GameType, Int) -> Unit)? = null,
    // Task 10: SIMULATION flow carries the full payload (roster + map + total
    // slots) selected on the new SimulationSettingsPage. The legacy callback
    // above is kept untouched for telemetry parity (Task 11 will gate UI
    // rotation based on the "primary" commander — both callbacks fire on
    // SIMULATION confirm).
    private val onSimulationConfirmSelection: ((List<String>, Int, String) -> Unit)? = null,
    private val onCancelSelection: (() -> Unit)? = null
) : SimplePanel(className = "commander-selection-overlay")
{
    private lateinit var titleLabel: io.kvision.html.H3
    private lateinit var step1Content: SimplePanel
    private lateinit var step2Content: SimplePanel
    private lateinit var step3Content: SimplePanel
    private var simulationSettingsPage: SimulationSettingsPage? = null
    private lateinit var backButton: Button
    private lateinit var cancelButton: Button
    private lateinit var nextButton: Button
    private lateinit var playButton: Button
    private var currentStep: Int = 1

    private var activeCard: CommanderCard? = null
    private var activeOpponentCard: OpponentCard? = null
    private var activeGameTypeCard: GameTypeCard? = null
    private var selectedCommanderValue: Commander? = null
    private var selectedAiOpponentCount: Int = 1
    private var selectedGameType: GameType = GameType.SINGLEPLAYER

    // Simulation-mode payload captured from SimulationSettingsPage (Step 3).
    // confirmAndPlay() reads these when forwarding to onConfirmSelection. They
    // stay at their defaults for SINGLEPLAYER / MULTIPLAYER / FRIEND — the
    // existing flow ignores them.
    private var selectedSimulationHumanPlayerNames: List<String> = emptyList()
    private var selectedSimulationMapPath: String = ""
    private var selectedSimulationTotalSlots: Int = 2
    private lateinit var searchField: TextInput
    private lateinit var noCommanderMatches: SimplePanel
    private lateinit var opponentSection: SimplePanel
    private lateinit var multiplayerFooter: io.kvision.html.P
    private lateinit var playersFooter: io.kvision.html.P
    private val commanderCardEntries = mutableListOf<CommanderCardEntry>()
    val selectedCommander: Commander?
        get() = selectedCommanderValue

    init
    {
        position = Position.FIXED
        top = 0.px
        left = 0.px
        width = 100.perc
        height = 100.perc
        zIndex = 9100
        background = Background(Color("rgba(0, 0, 0, 0.85)"))
        display = Display.FLEX
        justifyContent = JustifyContent.CENTER
        alignItems = AlignItems.CENTER

        vPanel(className = "commander-selection-window") {
            width = 1265.px
            // Height is intentionally not fixed in Kotlin. The CSS class
            // `.commander-selection-window` uses
            // `max-height: calc(100vh - 48px)` so the dialog adapts to the
            // viewport. Each wizard step shows one decision worth of content
            // so the dialog never has to compete for vertical space.
            padding = 24.px
            spacing = 16
            background = Background(Color("#0c101f"))
            border = Border(2.px, BorderStyle.SOLID, Color("#3f4b75"))
            borderRadius = 14.px

            titleLabel = h3("Select Commander") {
                textAlign = TextAlign.CENTER
                fontSize = CssSize(30, UNIT.px)
                color = Color("#f4f6fb")
                margin = CssSize(0, UNIT.px)
            }

            if(commanders.isEmpty())
            {
                p("No commanders available. Create one from the Collection screen first.") {
                    fontSize = CssSize(18, UNIT.px)
                    textAlign = TextAlign.CENTER
                    color = Color("#a0a8cc")
                    margin = CssSize(24, UNIT.px)
                }
            }
            else
            {
                commanderCardEntries.clear()

                // === Step 1: Commander selection ===
                // The section wrapper is a flex child of the dialog window.
                // It claims the remaining vertical space after the title and
                // footer. Its `overflow: hidden` plus the inner list's own
                // `overflow-y: auto` make the list the only scrollable region
                // on this step. All sizing (`flex`, `min-height`,
                // `overflow`) comes from the CSS class
                // `.commander-selection-step-1` — setting any of them inline
                // would override the CSS and break the flex layout.
                step1Content = vPanel(spacing = 12, className = "commander-selection-step-1")
                {
                    width = 100.perc

                    hPanel(
                        className = "collection-search",
                        spacing = 12,
                        alignItems = AlignItems.CENTER
                    ) {
                        width = 100.perc
                        icon("fas fa-search")
                        searchField = textInput {
                            width = 100.perc
                            placeholder = "Search commanders..."
                            onInput {
                                applyCommanderFilter()
                            }
                            setAttribute("spellcheck", "false")
                        }
                    }

                    vPanel(className = "commander-selection-list") {
                        // The list is the only scrollable region on Step 1.
                        // It fills the available vertical space (whatever
                        // the section wrapper above gives it) and scrolls
                        // internally when there are more commanders than
                        // fit. The previous fixed 400px height was removed
                        // because the Step 1 layout now has the entire
                        // dialog to itself.
                        flexGrow = 1
                        flexShrink = 1
                        minHeight = 0.px
                        overflowY = Overflow.AUTO
                        spacing = 12
                        commanders.forEach { commander ->
                            val card = CommanderCard(commander) { commanderCard ->
                                handleCommanderSelection(commanderCard)
                            }
                            commanderCardEntries.add(CommanderCardEntry(commander.name.lowercase(), card.cardPanel))
                            add(card.cardPanel)
                        }
                        noCommanderMatches = p("No commanders match that search.") {
                            fontSize = CssSize(16, UNIT.px)
                            textAlign = TextAlign.CENTER
                            color = Color("#bec6e3")
                            display = Display.NONE
                            marginTop = 12.px
                        }
                    }

                    applyCommanderFilter()
                }
                add(step1Content)

                // === Step 2: Game settings ===
                // Built upfront and hidden until the user clicks Next. This
                // way the GameType / OpponentCard / footer state is set up
                // once at dialog open time, not lazily on step transition.
                step2Content = buildStep2Content()
                step2Content.addCssClass("commander-selection-step-2")
                step2Content.display = Display.NONE
                add(step2Content)

                // === Step 3: Simulation Settings ===
                // Real content from SimulationSettingsPage. The page collects
                // the human-roster + total slot count + map path, then fires
                // onPlayClicked which stores the payload on the dialog so
                // confirmAndPlay() can forward it to onConfirmSelection.
                step3Content = SimulationSettingsPage(
                    savedCommanders = commanders,
                    onPlayClicked = { names, totalSlots, mapPath ->
                        // Single-Play contract 2026-08-08: the inner page
                        // no longer renders a Play button, so this
                        // callback is unused in production. Kept for
                        // binary-compat with any external callers. The
                        // dialog reads state via
                        // SimulationSettingsPage.currentPayload() on the
                        // outer footer Play click instead.
                        @Suppress("UNUSED_PARAMETER")
                        val unused = Triple(names, totalSlots, mapPath)
                    },
                    // Single-Play contract 2026-08-08: every roster / map /
                    // slot change on the page must re-evaluate the dialog
                    // footer Play's `disabled` property. Without this
                    // hook, the gate reads the page's state at step
                    // transition only and stays stale across config
                    // changes.
                    onStateChanged = ::applyStepVisibility
                )
                step3Content.display = Display.NONE
                add(step3Content)
                // Hold a reference so the dialog footer Play can pull
                // state on click — single-Play contract 2026-08-08.
                simulationSettingsPage = step3Content as? SimulationSettingsPage
                    ?: error("step3Content is not a SimulationSettingsPage; check the wiring")
            }

            // === Footer buttons ===
            // Always visible: Cancel. Conditionally visible: Back (step 2),
            // Next (step 1), Play (step 2). The Play button replaces the
            // previous OK button and is enabled only after a commander is
            // selected (the selected commander carries over from Step 1).
            hPanel(spacing = 16, alignItems = AlignItems.CENTER, justify = JustifyContent.CENTER) {
                width = CssSize(100, UNIT.perc)
                marginTop = 4.px

                cancelButton = button("Cancel") {
                    width = CssSize(200, UNIT.px)
                    height = CssSize(56, UNIT.px)
                    addCssClass("btn-secondary-action")
                    onClick {
                        clearSelection()
                        onCancelSelection?.invoke()
                        this@CommanderSelectionDialog.hide()
                    }
                }

                backButton = button("Back") {
                    width = CssSize(200, UNIT.px)
                    height = CssSize(56, UNIT.px)
                    addCssClass("btn-secondary-action")
                    display = Display.NONE
                    onClick {
                        goToStep(1)
                    }
                }

                nextButton = button("Next") {
                    width = CssSize(200, UNIT.px)
                    height = CssSize(56, UNIT.px)
                    addCssClass("btn-secondary-action")
                    disabled = true
                    onClick {
                        // Branch on game type: SIMULATION goes to the new
                        // Simulation Settings page (Step 3) instead of
                        // stopping at Step 2's Play button. All other game
                        // types keep the original Step 1 → Step 2 path.
                        val nextStep = if(selectedGameType == GameType.SIMULATION) 3 else 2
                        Logger.info(
                            LogCategory.UI,
                            "CommanderSelectionDialog: Next clicked, gameType=$selectedGameType → step=$nextStep"
                        )
                        goToStep(nextStep)
                    }
                }

                playButton = button("Play") {
                    width = CssSize(200, UNIT.px)
                    height = CssSize(56, UNIT.px)
                    addCssClass("btn-secondary-action")
                    display = Display.NONE
                    disabled = true
                    onClick {
                        confirmAndPlay()
                    }
                }
            }
        }

        // Note: The mobile-portrait CSS rules live in night-mode.css under the
        // @media (max-width: 600px) block at the end of the file. We do not
        // mirror them via KVision's Style.create DSL here because the
        // io.kvision.core.Style class in kvision-js 9.1.1 does not expose a
        // static `create` method to the Kotlin/JS target (the documented API
        // path is on a separate kvision sub-module not present in the JS-only
        // runtime). The CSS file approach is the load-bearing source of truth
        // and is loaded by the browser before this widget mounts.
        // For CommanderSelectionDialog, the same CSS file is used because
        // commander-selection-overlay is shared with ResumeOrNewDialog.

        // Live rotation handling: when the viewport width crosses the 600px
        // breakpoint (typically a phone rotation), update the mobile-layout
        // attribute on the root so e2e probes can verify the layout state and
        // any future code that needs to react to layout change has a stable hook.
        GlobalScope.launch {
            try {
                val mediaQuery = kotlinx.browser.window.matchMedia("(max-width: 600px), (max-height: 600px) and (orientation: portrait)")
                val updateAttribute: (Boolean) -> Unit = { matches ->
                    setAttribute("data-mobile-layout", if (matches) "portrait" else "desktop")
                }
                updateAttribute(mediaQuery.matches)
                mediaQuery.addEventListener("change", { event ->
                    val mql = event.asDynamic()
                    updateAttribute(mql.matches as Boolean)
                })
                if (getAttribute("data-testid") == null) {
                    setAttribute("data-testid", "commander-selection-root")
                }
                Logger.info(LogCategory.UI, "CommanderSelectionDialog: matchMedia listener attached, current state=${if (mediaQuery.matches) "portrait" else "desktop"}")
            }
            catch (err: Throwable) {
                Logger.warn(LogCategory.UI, "CommanderSelectionDialog: matchMedia listener failed to attach (non-fatal): ${err.message}")
            }
        }
    }

    /**
     * Builds the Step 2 content panel: Game Type selector (Single Player /
     * Multiplayer) and the Match Configuration section (AI opponent count,
     * hidden in Multiplayer). The footer summary line ("Players: 2" or
     * "PvP — match pool: pvp-2") is also rendered here so it lives next
     * to the controls it summarizes.
     */
    private fun buildStep2Content(): SimplePanel
    {
        return vPanel(spacing = 16)
        {
            width = 100.perc
            flexGrow = 1
            minHeight = 0.px

            h4("Game Type") {
                textAlign = TextAlign.CENTER
                fontSize = CssSize(24, UNIT.px)
                color = Color("#f4f6fb")
                marginBottom = 4.px
            }

            hPanel(spacing = 20, justify = JustifyContent.CENTER) {
                width = 100.perc

                val singlePlayerCard = GameTypeCard(
                    gameType = GameType.SINGLEPLAYER,
                    description = "Play vs. AI locally",
                    iconName = "fas fa-robot"
                ) { handleGameTypeSelection(it) }
                val multiplayerCard = GameTypeCard(
                    gameType = GameType.MULTIPLAYER,
                    description = "PVP",
                    iconName = "fas fa-globe"
                ) { handleGameTypeSelection(it) }
                // SIMULATION routes the user to a third wizard step
                // (Simulation Settings). The Next button on Step 2 branches
                // to Step 3 instead of stopping at Play. The card itself
                // has no special rendering — only the click→state→Next flow
                // matters. The full SimulationSettingsPage widget (Task 8b)
                // replaces the step3Content placeholder.
                val simulationCard = GameTypeCard(
                    gameType = GameType.SIMULATION,
                    description = "Play every slot yourself in a single match",
                    iconName = "fas fa-flask"
                ) { handleGameTypeSelection(it) }

                add(singlePlayerCard.cardPanel)
                add(multiplayerCard.cardPanel)
                add(simulationCard.cardPanel)

                // Default selection
                singlePlayerCard.setActive(true)
                activeGameTypeCard = singlePlayerCard
                selectedGameType = GameType.SINGLEPLAYER
            }

            // Match Configuration (AI opponents) — hidden in Multiplayer.
            opponentSection = vPanel(spacing = 12) {
                width = 100.perc
                flexGrow = 1
                minHeight = 0.px

                h4("Match Configuration") {
                    textAlign = TextAlign.CENTER
                    fontSize = CssSize(24, UNIT.px)
                    color = Color("#f4f6fb")
                    marginBottom = 4.px
                }

                hPanel(spacing = 20, justify = JustifyContent.CENTER) {
                    width = 100.perc

                    val card1 = OpponentCard(1, "1 vs 1: Duel") { handleOpponentSelection(it) }
                    val card2 = OpponentCard(2, "1 vs 2: Triangle") { handleOpponentSelection(it) }
                    val card3 = OpponentCard(3, "1 vs 3: Standard") { handleOpponentSelection(it) }

                    add(card1.cardPanel)
                    add(card2.cardPanel)
                    add(card3.cardPanel)

                    card1.setActive(true)
                    activeOpponentCard = card1
                    selectedAiOpponentCount = 1
                }
            }

            // Footer summary line that changes based on game type.
            hPanel(spacing = 16, alignItems = AlignItems.CENTER, justify = JustifyContent.CENTER) {
                width = 100.perc

                playersFooter = p("Players: 2") {
                    fontSize = CssSize(18, UNIT.px)
                    color = Color("#92a4d4")
                    textAlign = TextAlign.CENTER
                    margin = CssSize(0, UNIT.px)
                }

                multiplayerFooter = p("PvP — match pool: ${MatchmakingClient.DEFAULT_MULTIPLAYER_MATCH_POOL}") {
                    fontSize = CssSize(18, UNIT.px)
                    color = Color("#92a4d4")
                    textAlign = TextAlign.CENTER
                    margin = CssSize(0, UNIT.px)
                    display = Display.NONE
                }
                add(playersFooter)
                add(multiplayerFooter)
            }
        }
    }

    private fun goToStep(step: Int)
    {
        Logger.info(LogCategory.UI, "CommanderSelectionDialog: goToStep($step) from currentStep=$currentStep")
        currentStep = step
        applyStepVisibility()
    }

    private fun applyStepVisibility()
    {
        val isStep1 = currentStep == 1
        val isStep2 = currentStep == 2
        val isStep3 = currentStep == 3
        val isSimulation = selectedGameType == GameType.SIMULATION

        // Title changes to indicate which decision the user is on.
        titleLabel.content = when(currentStep)
        {
            1 -> "Select Commander"
            2 -> "Choose Game Settings"
            3 -> "Simulation Settings"
            else -> ""
        }

        // Only one step's content is in the layout at a time.
        step1Content.display = if(isStep1) Display.FLEX else Display.NONE
        step1Content.visible = isStep1
        step2Content.display = if(isStep2) Display.FLEX else Display.NONE
        step2Content.visible = isStep2
        step3Content.display = if(isStep3) Display.FLEX else Display.NONE
        step3Content.visible = isStep3

        // Back is hidden on Step 1 (no previous step). Play/Next on Step 2
        // depend on the selected game type: SIMULATION shows Next on Step 2
        // (jumping to Step 3) and Play on Step 3; the other game types
        // show Play on Step 2 and Next is hidden.
        // Note: Back and Next/Play keep their original display values
        // (FLEX for Back on step 2+, INLINE for Play/Next on their active
        // states) so the dialog CSS layout is unchanged.
        backButton.display = if(isStep1) Display.NONE else Display.FLEX
        backButton.visible = !isStep1
        nextButton.display = if(isStep2 && isSimulation) Display.INLINE else Display.NONE
        nextButton.visible = isStep2 && isSimulation
        // Single-Play contract 2026-08-08: the outer footer Play is the
        // only Play button on the simulation page. Visible on Step 2
        // (SINGLEPLAYER / MULTIPLAYER dispatch) AND on Step 3
        // (SIMULATION dispatch — the inner page Play is removed). On
        // Step 2 + SIMULATION, the Next button routes forward to
        // Step 3, so Play is hidden here (matches the 2026-08-07 fix
        // that hid the redundant outer Play when SIMULATION is picked
        // mid-step). The disabled property reads from the page's
        // `isReadyForConfirm()` gate via `isPlayButtonReady()`.
        val isStep2SingleOrMultiplayer = isStep2 && selectedGameType != GameType.SIMULATION
        playButton.display = if(isStep3 || isStep2SingleOrMultiplayer) Display.INLINE else Display.NONE
        playButton.visible = isStep3 || isStep2SingleOrMultiplayer
        playButton.disabled = !isPlayButtonReady()
    }

    private fun confirmAndPlay()
    {
        val commander = selectedCommanderValue ?: return
        if (selectedGameType == GameType.SIMULATION)
        {
            val page = simulationSettingsPage
            if (page == null)
            {
                Logger.warn(
                    LogCategory.NETWORK,
                    "CommanderSelectionDialog.confirmAndPlay: SIMULATION mode but SimulationSettingsPage is not mounted — bailing out"
                )
                return
            }
            if (!page.isReadyForConfirm())
            {
                Logger.warn(
                    LogCategory.NETWORK,
                    "CommanderSelectionDialog.confirmAndPlay: SIMULATION validation gate failed; payload invalid"
                )
                return
            }
            val (roster, totalSlots, mapPath) = page.currentPayload()
            // Keep the dialog state in sync so other call sites
            // (e.g. simulateSimulationPlayForTest) read the same values.
            selectedSimulationHumanPlayerNames = roster
            selectedSimulationTotalSlots = totalSlots
            selectedSimulationMapPath = mapPath
            Logger.info(
                LogCategory.NETWORK,
                "CommanderSelectionDialog: confirmAndPlay SIMULATION commander='${commander.name}' roster=$selectedSimulationHumanPlayerNames slots=$selectedSimulationTotalSlots map='$selectedSimulationMapPath'"
            )
            // Single-Play contract 2026-08-08: the outer footer Play is
            // the only Play button on the simulation page. It pulls
            // state from SimulationSettingsPage on click.
            onSimulationConfirmSelection?.invoke(
                selectedSimulationHumanPlayerNames,
                selectedSimulationTotalSlots,
                selectedSimulationMapPath
            )
        }
        onConfirmSelection?.invoke(commander, selectedGameType, selectedAiOpponentCount)
        this@CommanderSelectionDialog.hide()
    }

    private fun handleCommanderSelection(card: CommanderCard)
    {
        if(activeCard === card)
        {
            clearSelection()
            return
        }

        activeCard?.setActive(false)
        card.setActive(true)
        activeCard = card
        selectedCommanderValue = card.commander
        nextButton.disabled = false
    }

    private fun handleOpponentSelection(card: OpponentCard)
    {
        if(activeOpponentCard === card) return

        activeOpponentCard?.setActive(false)
        card.setActive(true)
        activeOpponentCard = card
        selectedAiOpponentCount = card.aiCount
        updatePlayersFooter()
    }

    private fun handleGameTypeSelection(card: GameTypeCard)
    {
        if(activeGameTypeCard === card) return

        activeGameTypeCard?.setActive(false)
        card.setActive(true)
        activeGameTypeCard = card
        selectedGameType = card.gameType
        applyGameTypeVisibility()
    }

    private fun applyGameTypeVisibility()
    {
        val isMultiplayer = selectedGameType == GameType.MULTIPLAYER
        opponentSection.display = if(isMultiplayer) Display.NONE else Display.FLEX
        opponentSection.visible = !isMultiplayer
        multiplayerFooter.display = if(isMultiplayer) Display.FLEX else Display.NONE
        multiplayerFooter.visible = isMultiplayer
        playersFooter.display = if(isMultiplayer) Display.NONE else Display.FLEX
        playersFooter.visible = !isMultiplayer
        // Bug fix 2026-08-07: game-type changes mid-step must also refresh
        // the Next / Play footer buttons. applyStepVisibility() owns the
        // footer-button visibility rules (Next is INLINE only when
        // isStep2 && isSimulation; Play is INLINE on isStep3 OR on
        // isStep2 && !isSimulation). Without this call, picking
        // Simulation on Step 2 leaves Next hidden and Play visible —
        // the user clicks Play, confirmAndPlay fires with the empty
        // simulation payload, MainMenu.beginSimulationMatchSession:340
        // bails out silently, and the dialog closes without starting
        // the game. Pinned by
        // CommanderSelectionDialogSimulationPageTest."simulation
        // selection on step 2 surfaces next button and hides play button".
        applyStepVisibility()
    }

    private fun updatePlayersFooter()
    {
        if(::playersFooter.isInitialized)
        {
            playersFooter.content = "Players: ${selectedAiOpponentCount + 1}"
        }
    }

    private fun clearSelection()
    {
        selectedCommanderValue = null
        activeCard?.setActive(false)
        activeCard = null
        nextButton.disabled = true
        if(::playButton.isInitialized) playButton.disabled = true
    }

    // === Test seams ===
    //
    // These four functions exist solely to let the jsBrowserTest suite
    // drive the dialog's state machine without going through real DOM
    // click events. They are `internal` so jsTest can see them but no
    // production caller ever has a reason to. The KVision DSL builds
    // the real widget DOM inside `init {}` and assigns the `step{N}Content`
    // / buttons / cards, so by the time these functions are called the
    // widget is fully wired — the seams just bypass the click handlers.
    //
    // Why: jsBrowserTest runs the real Kotlin/JS code under headless
    // Chromium, but synthesizing native `MouseEvent` dispatch into a
    // SimplePanel that was never attached to a parent container is
    // fragile (KVision creates the DOM element lazily on
    // parent-attachment). Driving state directly is faster, simpler,
    // and still exercises every line of applyStepVisibility().

    /**
     * Returns the content panel for the requested wizard step (1/2/3),
     * or `null` if the step number is out of range. Exposed for
     * jsBrowserTest so tests can assert on `display` and `visible`.
     */
    internal fun stepContentForTest(step: Int): SimplePanel?
    {
        return when(step)
        {
            1 -> if(::step1Content.isInitialized) step1Content else null
            2 -> if(::step2Content.isInitialized) step2Content else null
            3 -> if(::step3Content.isInitialized) step3Content else null
            else -> null
        }
    }

    /**
     * Returns the Next button so jsBrowserTest can assert on its
     * display/visible state under different game-type branches.
     */
    internal fun nextButtonForTest(): Button
    {
        return nextButton
    }

    /**
     * Returns the Play button so jsBrowserTest can assert on its
     * display/visible state under different game-type branches.
     */
    internal fun playButtonForTest(): Button
    {
        return playButton
    }

    /**
     * Drives [handleGameTypeSelection] directly so tests can put the
     * dialog into SIMULATION/SINGLEPLAYER state without dispatching
     * a synthetic click on a GameTypeCard.
     */
    internal fun simulateGameTypeSelection(type: GameType)
    {
        val card = GameTypeCard(
            gameType = type,
            description = "test-fixture",
            iconName = "fas fa-test"
        ) { handleGameTypeSelection(it) }
        handleGameTypeSelection(card)
    }

    /**
     * Drives [handleCommanderSelection] directly so tests can enable
     * the Next button on Step 1 without dispatching a synthetic click
     * on a CommanderCard.
     */
    internal fun simulateCommanderSelection(commander: Commander)
    {
        val card = CommanderCard(commander) { handleCommanderSelection(it) }
        handleCommanderSelection(card)
    }

    /**
     * Drives the Next button's onClick path directly. Equivalent to
     * a user click but bypasses DOM event dispatch.
     */
    internal fun simulateNextClick()
    {
        val nextStep = if(selectedGameType == GameType.SIMULATION) 3 else 2
        Logger.info(
            LogCategory.UI,
            "CommanderSelectionDialog: simulateNextClick, gameType=$selectedGameType → step=$nextStep"
        )
        goToStep(nextStep)
    }

    /**
     * Task 10 test seam: drives the SIMULATION Play-click path without
     * depending on [SimulationSettingsPage] internals. Synthesizes a
     * payload (roster / slots / map) as if the user picked it on Step 3,
     * stores it on the dialog state, then invokes [confirmAndPlay].
     * This is what the [SimulationSettingsPage] Play button does in
     * production: store payload → call dialog.confirmAndPlay().
     *
     * Returns the captured payload triple so tests can assert that
     * the onSimulationConfirmSelection callback received exactly what
     * was stored. Returns `null` when the dialog is not in SIMULATION
     * mode (caller error guard).
     */
    internal fun simulateSimulationPlayForTest(
        roster: List<String>,
        totalSlots: Int,
        mapPath: String
    ): Triple<List<String>, Int, String>?
    {
        if (selectedGameType != GameType.SIMULATION)
        {
            return null
        }
        // Single-Play contract 2026-08-08: the production path is now
        // "user configures page → outer Play reads page state". The
        // test seam drives that path by configuring the page first
        // (toggle each roster name + select the map), then invoking
        // confirmAndPlay() which reads from the page.
        val page = simulationSettingsPage ?: return null
        // Reset the page to the supplied payload.
        // First, clear existing roster toggles to avoid duplicates
        // when the test runs in sequence.
        // (Test ordering is deterministic in this suite, but be
        // defensive: only toggle names that are not already selected.)
        val currentNames = page.currentPayload().first.toSet()
        for (name in currentNames)
        {
            page.simulateCommanderToggle(name)
        }
        for (name in roster)
        {
            page.simulateCommanderToggle(name)
        }
        page.simulateMapSelection(mapPath)
        page.simulateSlotCountChange(totalSlots)
        if (!page.isReadyForConfirm())
        {
            Logger.warn(
                LogCategory.NETWORK,
                "CommanderSelectionDialog.simulateSimulationPlayForTest: supplied payload did not pass validation gate; bailing out"
            )
            return null
        }
        confirmAndPlay()
        return Triple(roster, totalSlots, mapPath)
    }

    /**
     * Single-Play contract 2026-08-08: drives the OUTER dialog footer
     * Play button (the only Play button on the simulation page). The
     * outer Play reads the page's current payload + validation gate on
     * click — see `confirmAndPlay()`. This seam mirrors the production
     * click path for tests that don't need to fake a DOM event on the
     * KVision Button widget.
     *
     * Returns the captured callback payload (or null when the dialog is
     * not in SIMULATION mode / the gate failed).
     */
    internal fun simulateOuterPlayClickForTest(): Triple<List<String>, Int, String>?
    {
        if (selectedGameType != GameType.SIMULATION)
        {
            return null
        }
        val page = simulationSettingsPage
        if (page == null || !page.isReadyForConfirm())
        {
            return null
        }
        confirmAndPlay()
        val captured = selectedSimulationHumanPlayerNames
        return Triple(captured, selectedSimulationTotalSlots, selectedSimulationMapPath)
    }

    /**
     * Test seam: returns the live SimulationSettingsPage instance so tests
     * can drive its config (roster toggle / map pick) and verify the
     * outer Play gate. Returns `null` when the dialog is not in
     * SIMULATION mode.
     */
    internal fun simulationSettingsPageForTest(): SimulationSettingsPage? =
        if (selectedGameType == GameType.SIMULATION) simulationSettingsPage
        else null

    override fun hide()
    {
        super.hide()
        clearSelection()
        this.parent?.remove(this)
    }

    /**
     * Single-Play contract 2026-08-08: the outer footer Play button is
     * gated by a per-step rule. SIMULATION Step 3 reads the page's
     * roster + map + slots validation. SINGLEPLAYER / MULTIPLAYER Step 2
     * just needs a commander picked.
     */
    private fun isPlayButtonReady(): Boolean
    {
        val commanderSelected = selectedCommanderValue != null
        return when
        {
            currentStep == 3 && selectedGameType == GameType.SIMULATION ->
                commanderSelected && (simulationSettingsPage?.isReadyForConfirm() ?: false)
            currentStep == 2 ->
                commanderSelected
            else -> false
        }
    }

    private fun applyCommanderFilter()
    {
        if(!this::searchField.isInitialized || !this::noCommanderMatches.isInitialized)
        {
            return
        }

        val filterValue = searchField.value?.trim()?.lowercase().orEmpty()
        var matchCount = 0
        commanderCardEntries.forEach { entry ->
            val matches = filterValue.isBlank() || entry.name.contains(filterValue)
            entry.panel.display = if(matches) Display.FLEX else Display.NONE
            entry.panel.visible = matches
            if(matches)
            {
                matchCount++
            }
        }
        val shouldShowEmptyState = matchCount == 0
        noCommanderMatches.visible = shouldShowEmptyState
        noCommanderMatches.display = if(shouldShowEmptyState) Display.FLEX else Display.NONE
    }

    private data class CommanderCardEntry(
        val name: String,
        val panel: SimplePanel
    )

    private class OpponentCard(
        val aiCount: Int,
        val description: String,
        private val onSelect: (OpponentCard) -> Unit
    )
    {
        val cardPanel: SimplePanel

        init
        {
            cardPanel = SimplePanel()
            cardPanel.addCssClass("commander-selection-card")
            cardPanel.width = 300.px
            cardPanel.padding = CssSize(15, UNIT.px)
            cardPanel.borderRadius = 10.px
            cardPanel.border = Border(1.px, BorderStyle.SOLID, Color("rgba(255, 255, 255, 0.08)"))
            cardPanel.cursor = Cursor.POINTER

            cardPanel.onClick {
                onSelect(this@OpponentCard)
            }

            cardPanel.vPanel(spacing = 4, alignItems = AlignItems.CENTER) {
                hPanel(spacing = 10, alignItems = AlignItems.CENTER) {
                    icon("fas fa-user-friends") {
                        fontSize = 24.px
                        color = Color("#5e6adc")
                    }

                    span("${aiCount + 1} Players") {
                        fontSize = CssSize(20, UNIT.px)
                        fontWeight = FontWeight.BOLD
                        color = Color("#ffffff")
                    }
                }

                p(description) {
                    fontSize = CssSize(14, UNIT.px)
                    color = Color("#cbd3ff")
                    textAlign = TextAlign.CENTER
                    margin = 0.px
                }
            }
        }

        fun setActive(active: Boolean)
        {
            if(active)
            {
                cardPanel.addCssClass("commander-selection-card-active")

                // One-time click animations
                cardPanel.addCssClass("commander-selection-card-click-flash")
                cardPanel.addCssClass("commander-selection-card-click-sheen")

                kotlinx.browser.window.setTimeout({
                    cardPanel.removeCssClass("commander-selection-card-click-flash")
                    cardPanel.removeCssClass("commander-selection-card-click-sheen")
                }, 800)
            }
            else
            {
                cardPanel.removeCssClass("commander-selection-card-active")
            }
        }
    }

    private class GameTypeCard(
        val gameType: GameType,
        val description: String,
        val iconName: String,
        private val onSelect: (GameTypeCard) -> Unit
    )
    {
        val cardPanel: SimplePanel

        init
        {
            cardPanel = SimplePanel()
            cardPanel.addCssClass("commander-selection-card")
            cardPanel.width = 360.px
            cardPanel.padding = CssSize(20, UNIT.px)
            cardPanel.borderRadius = 10.px
            cardPanel.border = Border(1.px, BorderStyle.SOLID, Color("rgba(255, 255, 255, 0.08)"))
            cardPanel.cursor = Cursor.POINTER

            cardPanel.onClick {
                onSelect(this@GameTypeCard)
            }

            cardPanel.vPanel(spacing = 6, alignItems = AlignItems.CENTER) {
                hPanel(alignItems = AlignItems.CENTER) {
                    icon(iconName) {
                        fontSize = 26.px
                        color = Color("#5e6adc")
                    }
                }

                p(description) {
                    fontSize = CssSize(14, UNIT.px)
                    color = Color("#cbd3ff")
                    textAlign = TextAlign.CENTER
                    margin = 0.px
                }
            }
        }

        fun setActive(active: Boolean)
        {
            if(active)
            {
                cardPanel.addCssClass("commander-selection-card-active")

                // One-time click animations
                cardPanel.addCssClass("commander-selection-card-click-flash")
                cardPanel.addCssClass("commander-selection-card-click-sheen")

                kotlinx.browser.window.setTimeout({
                    cardPanel.removeCssClass("commander-selection-card-click-flash")
                    cardPanel.removeCssClass("commander-selection-card-click-sheen")
                }, 800)
            }
            else
            {
                cardPanel.removeCssClass("commander-selection-card-active")
            }
        }
    }

    private class CommanderCard(
        val commander: Commander,
        private val onSelect: (CommanderCard) -> Unit
    )
    {
        val cardPanel: SimplePanel

        init
        {
            cardPanel = SimplePanel()
            cardPanel.addCssClass("commander-selection-card")
            cardPanel.padding = CssSize(18, UNIT.px)
            cardPanel.borderRadius = 10.px
            cardPanel.border = Border(1.px, BorderStyle.SOLID, Color("rgba(255, 255, 255, 0.08)"))
            cardPanel.cursor = Cursor.POINTER

            cardPanel.onClick {
                onSelect(this@CommanderCard)
            }

            cardPanel.vPanel(spacing = 6) {
                hPanel(spacing = 10, alignItems = AlignItems.CENTER) {
                    icon(iconForType(commander.type)) {
                        fontSize = 28.px
                        color = Color("#5e6adc")
                    }

                    span(commander.name.ifBlank { "Unnamed Commander" }) {
                        fontSize = CssSize(22, UNIT.px)
                        fontWeight = FontWeight.BOLD
                        color = Color("#ffffff")
                    }
                }

                span("Empire: ${commander.empire.ifBlank { "Not specified" }}") {
                    fontSize = CssSize(14, UNIT.px)
                    color = Color("#92a4d4")
                    addCssClass("commander-selection-card-meta")
                }

                span("Type: ${commander.type.name} • Trait: ${commander.trait.name}") {
                    fontSize = CssSize(14, UNIT.px)
                    color = Color("#92a4d4")
                    addCssClass("commander-selection-card-meta")
                }

                p(commander.description.ifBlank { "No description recorded for this commander yet." }) {
                    fontSize = CssSize(14, UNIT.px)
                    color = Color("#cbd3ff")
                    marginTop = 6.px
                    addCssClass("commander-selection-card-description")
                }
            }
        }

        fun setActive(active: Boolean)
        {
            if(active)
            {
                cardPanel.addCssClass("commander-selection-card-active")

                // Trigger one-time click animations
                cardPanel.addCssClass("commander-selection-card-click-flash")
                cardPanel.addCssClass("commander-selection-card-click-sheen")

                // Remove animation classes after completion
                kotlinx.browser.window.setTimeout({
                    cardPanel.removeCssClass("commander-selection-card-click-flash")
                    cardPanel.removeCssClass("commander-selection-card-click-sheen")
                }, 800)
            }
            else
            {
                cardPanel.removeCssClass("commander-selection-card-active")
                cardPanel.removeCssClass("commander-selection-card-click-flash")
                cardPanel.removeCssClass("commander-selection-card-click-sheen")
            }
        }

        private fun iconForType(type: CommanderType) = when(type)
        {
            CommanderType.Flying -> "fas fa-feather-alt"
            CommanderType.Aquatic -> "fas fa-water"
            CommanderType.Land -> "fas fa-mountain"
        }
    }
}
